import { CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import Select from "@/components/atoms/Select";
import Text from "@/components/atoms/Text";
import { AddRowButton, HeaderText, PhotoLine, RemoveRowButton, UnitNumberInput } from "./FormParts";
import { InspectionFormApi, InspectionSummary, isPerOrder } from "../useInspectionForm";
import { useInspectionFormat } from "../useInspectionFormat";
import { DeviationCosts } from "../useDeviationPreview";
import { InspectionErrors, MAX_DEVIATIONS, MAX_PHOTOS_PER_STREAM } from "../validation";
import styles from "../styles.module.css";

const COLUMNS = "minmax(0, 1fr) 130px 110px 36px";

/** The columns are narrow; the options ("Sorteringsgebyr · kr 950/t") get room to stay on one line. */
const WIDE_DROPDOWN = { width: 300, position: "bottom-start" } as const;

interface DeviationServicesTableProps {
  form: InspectionFormApi;
  summary: InspectionSummary;
  errors: InspectionErrors;
  costs: DeviationCosts | null;
}

/**
 * "Avvikstjenester": the work a deviation caused, picked from the deviation services on the original order's
 * contract and counted in the service's unit (hours, pieces …; a per-order fee has no number). Each row is
 * photographed.
 */
export default function DeviationServicesTable({ form, summary, errors, costs }: DeviationServicesTableProps) {
  const { t } = useTranslation();
  const format = useInspectionFormat();
  const { deviationServices } = form.values;
  const error = (path: string) => (errors[path] ? t(errors[path]) : undefined);

  const typeOptions = summary.deviationTypes.map((type) => ({
    value: String(type.id),
    label: `${type.name} · ${
      type.price
        ? format.unitPrice(type.price, type.currency, format.deviationUnit(type.unitCode), false)
        : t("inspections:deviations.documentationOnly")
    }`,
  }));

  return (
    <div className={styles.lineTable} style={{ "--line-columns": COLUMNS } as CSSProperties}>
      <div className={`${styles.lineGrid} ${styles.lineHeader}`}>
        <HeaderText>{t("inspections:deviations.service")}</HeaderText>
        <HeaderText>{t("inspections:deviations.registeredNumber")}</HeaderText>
        <HeaderText className={styles.alignRight}>{t("inspections:deviations.cost")}</HeaderText>
        <span />
      </div>

      {deviationServices.map((service, index) => {
        const type = summary.typeOf(service);
        const path = (field: string) => `deviationServices.${index}.${field}`;
        const cost = costs?.services[index];
        return (
          <div key={index} className={styles.lineRow} data-testid={`inspection-service-${index}`}>
            <div className={styles.lineGrid}>
              <Select
                className={styles.lineWide}
                aria-label={t("inspections:deviations.service")}
                placeholder={t("inspections:deviations.selectType")}
                data={typeOptions}
                comboboxProps={WIDE_DROPDOWN}
                value={service.deviationTypeId === null ? null : String(service.deviationTypeId)}
                onChange={(value) => form.updateService(index, { deviationTypeId: value ? Number(value) : null })}
                error={error(path("deviationTypeId"))}
                data-testid={`inspection-service-${index}-type`}
              />
              {isPerOrder(type) ? (
                <Text size="sm" color="secondary" data-testid={`inspection-service-${index}-per-order`}>
                  {t("inspections:deviations.perOrder")}
                </Text>
              ) : (
                <UnitNumberInput
                  aria-label={t("inspections:deviations.registeredNumber")}
                  unit={format.deviationUnit(type?.unitCode)}
                  value={service.registeredNumber}
                  onChange={(registeredNumber) => form.updateService(index, { registeredNumber })}
                  error={error(path("registeredNumber"))}
                  data-testid={`inspection-service-${index}-number`}
                />
              )}
              <Text
                size="sm"
                className={styles.alignRight}
                data-label={t("inspections:deviations.cost")}
                data-testid={`inspection-service-${index}-cost`}
              >
                {costs && cost !== undefined ? format.amount(cost, costs.currency) : "—"}
              </Text>
              <RemoveRowButton onClick={() => form.removeService(index)} label={t("inspections:deviations.remove")} />
            </div>
            <PhotoLine
              photos={service.photos}
              onChange={(photos) => form.updateService(index, { photos })}
              max={MAX_PHOTOS_PER_STREAM}
              error={error(path("photos"))}
              data-testid={`inspection-service-${index}-photos`}
            />
          </div>
        );
      })}

      <div className={styles.lineFooter}>
        <div className={styles.lineFooterStart}>
          <AddRowButton
            onClick={form.addService}
            disabled={!summary.canAddServices || summary.deviationRowCount >= MAX_DEVIATIONS}
            data-testid="inspection-add-service"
          >
            {t("inspections:deviations.addService")}
          </AddRowButton>
          {!summary.canAddServices && (
            <Text size="xs" color="tertiary" data-testid="inspection-no-services">
              {t("inspections:deviations.noServices")}
            </Text>
          )}
        </div>
        {costs && deviationServices.length > 0 && (
          <Text size="sm" data-testid="inspection-services-sum">
            {t("inspections:deviations.servicesSum", { amount: format.amount(costs.servicesTotal, costs.currency) })}
          </Text>
        )}
      </div>
    </div>
  );
}
